import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { updatePassword } from '../service/profileService'
import { parseServerErrors } from './parseServerErrors'

const MIN_PASSWORD_LENGTH = 6

interface PasswordFormState {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

const INITIAL_FORM: PasswordFormState = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
}

export function useChangePassword() {
  const { t } = useTranslation()
  const [form, setForm] = useState<PasswordFormState>(INITIAL_FORM)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  function updateField(field: string, value: string) {
    setForm(prev => ({ ...prev, [field]: value }))
    setErrors(prev => {
      if (!prev[field]) return prev
      const { [field]: _, ...rest } = prev
      return rest
    })
  }

  function resetForm() {
    setForm(INITIAL_FORM)
    setErrors({})
  }

  async function handleSave(): Promise<boolean> {
    const nextErrors: Record<string, string> = {}

    if (!form.currentPassword) nextErrors.currentPassword = t('my_profile.current_password_required')
    if (!form.newPassword) nextErrors.newPassword = t('my_profile.new_password_required')
    if (!form.confirmPassword) nextErrors.confirmPassword = t('my_profile.confirm_password_required')

    if (form.newPassword && form.newPassword.length < MIN_PASSWORD_LENGTH) {
      nextErrors.newPassword = t('my_profile.password_min_length', { min: MIN_PASSWORD_LENGTH })
    }

    if (form.newPassword && form.confirmPassword && form.newPassword !== form.confirmPassword) {
      nextErrors.confirmPassword = t('my_profile.passwords_mismatch')
    }

    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return false

    setSaving(true)
    try {
      await updatePassword({
        currentPassword: form.currentPassword,
        password: form.newPassword,
        passwordConfirmation: form.confirmPassword,
      })
      resetForm()
      return true
    } catch (err: unknown) {
      console.error('[MyProfile] password change failed:', err)
      const serverErrors = parseServerErrors(err)
      if (serverErrors) setErrors(serverErrors)
      return false
    } finally {
      setSaving(false)
    }
  }

  return { form, errors, saving, updateField, resetForm, handleSave }
}
